/**
 * Bidirectional mapping between SM1 and SM2 goal-metric identifiers.
 *
 * SM1 (legacy) stores goal enablement in the `_preference_set._value` JSON blob
 * keyed by the `UserGoals` enum values. SM2 stores enablement as rows in
 * `goal_preferences` keyed by `GoalMetricName` (see goal-metrics.ts). Almost
 * every metric uses the same identifier in both systems; only two diverge:
 *
 *   SM1 `monthlyProfit`     <-> SM2 `monthlyGP`
 *   SM1 `grossProfitMargin` <-> SM2 `monthlyGrossProfitMargin`
 *
 * This map is the single source of truth for that translation. It is consumed
 * by every place that keeps the two systems in sync:
 *   - the goal_preferences reconcile/backfill migration (config-api),
 *   - the SM2 -> SM1 enablement write-back (`Sm1GoalPrefSyncService` in crm-api),
 *   - and is mirrored verbatim in SM1 for the SM1 -> SM2 `materializeGoalPreferences`
 *     dual-write (SM1 lives in a separate repo and cannot import this).
 *
 * SM2-only metrics (e.g. `dailyUniqueItems`, which has no backing `goal` column)
 * have no SM1 counterpart and are intentionally absent from the map.
 *
 * NOTE: this entire mirror is a transitional shim. Once every tenant is off SM1
 * it can be deleted along with the dual-write/write-back code that uses it.
 */
export const SM1_TO_SM2_GOAL_KEY: Readonly<Record<string, string>> = {
	monthlyProfit: 'monthlyGP',
	grossProfitMargin: 'monthlyGrossProfitMargin',
	monthlyRevenue: 'monthlyRevenue',
	monthlyQuotes: 'monthlyQuotes',
	monthlyCustomers: 'monthlyCustomers',
	monthlyCustomersClosed: 'monthlyCustomersClosed',
	dailyCalls: 'dailyCalls',
	dailyProductiveCalls: 'dailyProductiveCalls',
	dailyCustomers: 'dailyCustomers',
	dailyLines: 'dailyLines',
	dailyCallDuration: 'dailyCallDuration',
	dailyLeadVisits: 'dailyLeadVisits',
	dailyCustomerVisits: 'dailyCustomerVisits',
};

/**
 * Reverse of {@link SM1_TO_SM2_GOAL_KEY}: SM2 `goal_preferences.goal` name ->
 * SM1 preference key. Used by the SM2 -> SM1 write-back.
 */
export const SM2_TO_SM1_GOAL_KEY: Readonly<Record<string, string>> =
	Object.freeze(
		Object.fromEntries(
			Object.entries(SM1_TO_SM2_GOAL_KEY).map(([sm1Key, sm2Key]) => [
				sm2Key,
				sm1Key,
			]),
		),
	);

/**
 * Translate an SM1 preference key to its SM2 `goal_preferences.goal` name.
 * Returns `undefined` for keys with no SM2 counterpart.
 */
export function sm1GoalKeyToSm2(sm1Key: string): string | undefined {
	return SM1_TO_SM2_GOAL_KEY[sm1Key];
}

/**
 * Translate an SM2 `goal_preferences.goal` name to its SM1 preference key.
 * Returns `undefined` for SM2-only metrics with no SM1 counterpart.
 */
export function sm2GoalKeyToSm1(sm2Key: string): string | undefined {
	return SM2_TO_SM1_GOAL_KEY[sm2Key];
}
